import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  FormControl,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Switch,
  Text,
  type SystemStyleObject
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { callToGetDealerStoreDetails, clearDealerStoreDetails } from '@/dealerProfile/ducks/slice';
import type { RootState } from '@/redux/store';
import { EditValuesToBeSavedInterface } from '@/model/editDetails.mjs';
import './store-optin-config.scss';

type StoreOptInStatus = 'opt_in' | 'neutral' | 'opt_out';

type StoreRow = {
  store_id?: string;
  storeName?: string;
  store_name?: string;
  isBlackListed?: boolean;
  isBlacklisted?: boolean;
  is_blacklisted?: boolean;
  isOptedIn?: boolean;
  isOptedOut?: boolean;
};

const STATUS_OPTIONS: Array<{ value: StoreOptInStatus; label: string; dotClassName: string }> = [
  { value: 'opt_in', label: 'Opt In', dotClassName: 'dot--opt-in' },
  { value: 'neutral', label: 'Neutral', dotClassName: 'dot--neutral' },
  { value: 'opt_out', label: 'Opt Out', dotClassName: 'dot--opt-out' }
];
const SELECTABLE_STATUS_OPTIONS = STATUS_OPTIONS.filter((option) => option.value !== 'neutral');

function getOptInStatusMenuButtonSx(
  status: StoreOptInStatus,
  options?: { isReadOnly?: boolean }
): SystemStyleObject {
  const isReadOnly = Boolean(options?.isReadOnly);
  const reset: SystemStyleObject = {
    bg: '#fff',
    borderRadius: '4px',
    boxShadow: 'none !important',
    outline: 'none',
    cursor: isReadOnly ? 'default' : undefined,
    pointerEvents: isReadOnly ? 'none' : undefined,
    opacity: isReadOnly ? 0.9 : undefined,
    _focus: { boxShadow: 'none !important' },
    _focusVisible: { boxShadow: 'none !important' }
  };

  if (status === 'opt_in') {
    const borderColor = isReadOnly ? 'rgba(0, 130, 54, 0.28)' : 'rgba(0, 130, 54, 0.5)';
    const color = '#008236';
    return {
      ...reset,
      color,
      fontWeight: isReadOnly ? 500 : 600,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor,
      _hover: isReadOnly
        ? { bg: '#fff', borderColor, color }
        : { bg: '#fff', borderColor },
      _active: isReadOnly
        ? { bg: '#fff', borderColor, color }
        : { bg: '#fff', borderColor }
    };
  }

  if (status === 'opt_out') {
    const borderColor = isReadOnly ? 'rgba(197, 48, 48, 0.28)' : 'rgba(197, 48, 48, 0.5)';
    const color = '#c53030';
    return {
      ...reset,
      color,
      fontWeight: isReadOnly ? 500 : 600,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor,
      _hover: isReadOnly
        ? { bg: '#fff', borderColor, color }
        : { bg: '#fff', borderColor },
      _active: isReadOnly
        ? { bg: '#fff', borderColor, color }
        : { bg: '#fff', borderColor }
    };
  }

  const borderColor = '#d1d5dc';
  return {
    ...reset,
    color: '#4A5565',
    fontWeight: 400,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor,
    _hover: { bg: '#fff', borderColor },
    _active: { bg: '#fff', borderColor }
  };
}

const BLACKLIST_SWITCH_SX: SystemStyleObject = {
  '& .chakra-switch__track': {
    bg: '#dedede',
    boxShadow: 'none'
  },
  '& .chakra-switch__track[data-checked]:not([data-theme])': {
    bg: '#c6f6d5'
  },
  '& .chakra-switch__thumb': {
    bg: '#ffffff',
    boxShadow: 'none'
  },
  '& .chakra-switch__thumb[data-checked]:not([data-theme])': {
    bg: '#48bb78'
  }
};

const getStoreId = (row: StoreRow) => row.store_id || row.store_name || row.storeName || '';

const getDisplayStoreName = (row: StoreRow) => row.storeName || row.store_name || '-';

const getDefaultBlacklistValue = (row: StoreRow) =>
  Boolean(row.isBlackListed ?? row.isBlacklisted ?? row.is_blacklisted ?? false);

/** Per-store status from API only (no dealer-level global override). */
const getOptInStatusFromApi = (row: StoreRow): StoreOptInStatus => {
  const optedIn = Boolean(row.isOptedIn);
  const optedOut = Boolean(row.isOptedOut);
  if (optedIn) return 'opt_in';
  if (optedOut) return 'opt_out';
  return 'neutral';
};

const normalizeStoreDetailsResponse = (payload: unknown): StoreRow[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.items)) {
      return obj.items as StoreRow[];
    }
    if (Array.isArray(obj.data)) {
      return obj.data as StoreRow[];
    }
    if (Array.isArray(obj.stores)) {
      return obj.stores as StoreRow[];
    }
  }
  return [];
};

type StoreOptInConfigProps = {
  dealerId?: string;
  dealerProfileId?: string;
  licenseKey?: string;
  /**
   * When false, per-store opt-in/out is read-only; label reflects API isOptedIn / isOptedOut.
   * Blacklist toggles stay editable.
   */
  allowPerStoreOptInStatusChange?: boolean;
  onStoreActionsChange?: (actions: EditValuesToBeSavedInterface[], hasChanges: boolean) => void;
};

export default function StoreOptInConfig({
  dealerId,
  dealerProfileId,
  licenseKey,
  allowPerStoreOptInStatusChange = false,
  onStoreActionsChange
}: StoreOptInConfigProps) {
  const dispatch = useDispatch();
  const dealerStoreDetails = useSelector((state: RootState) => state.dealer.dealerStoreDetails);
  const isLoading = useSelector((state: RootState) => state.dealer.dealerStoreDetailsLoading);
  const isApiError = useSelector((state: RootState) => state.dealer.dealerStoreDetailsError);

  const [searchText, setSearchText] = useState('');
  const [submittedSearchText, setSubmittedSearchText] = useState('');
  const [storeStatusById, setStoreStatusById] = useState<Record<string, StoreOptInStatus>>({});
  const [blacklistByStoreId, setBlacklistByStoreId] = useState<Record<string, boolean>>({});

  const onSearchSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSubmittedSearchText(searchText.trim());
    },
    [searchText]
  );

  const onClearSearch = useCallback(() => {
    setSearchText('');
    setSubmittedSearchText('');
  }, []);

  const showClearSearch = searchText.trim().length > 0;

  useEffect(() => {
    if (!dealerId) {
      dispatch(clearDealerStoreDetails());
      return;
    }
    dispatch(callToGetDealerStoreDetails(dealerId));
  }, [dealerId, dispatch]);

  useEffect(() => {
    setStoreStatusById({});
    setBlacklistByStoreId({});
  }, [dealerId]);

  const rows = useMemo(() => {
    const items = normalizeStoreDetailsResponse(dealerStoreDetails);
    const unique = new Map<string, StoreRow>();
    items.forEach((row) => {
      const id = getStoreId(row);
      if (id) {
        unique.set(id, row);
      }
    });
    return Array.from(unique.values());
  }, [dealerStoreDetails]);

  const tableRows = useMemo(() => {
    if (!submittedSearchText) {
      return rows;
    }
    const q = submittedSearchText.toLowerCase();
    return rows.filter((row) => getDisplayStoreName(row).toLowerCase().includes(q));
  }, [rows, submittedSearchText]);

  const storeActions = useMemo(() => {
    if (!licenseKey) {
      return [];
    }

    const actions: EditValuesToBeSavedInterface[] = [];
    rows.forEach((row) => {
      const storeId = getStoreId(row);
      if (!storeId) {
        return;
      }

      const apiStatus = getOptInStatusFromApi(row);
      const selectedStatus = allowPerStoreOptInStatusChange
        ? storeStatusById[storeId] ?? apiStatus
        : apiStatus;
      if (selectedStatus !== apiStatus) {
        if (selectedStatus === 'opt_in' || selectedStatus === 'opt_out') {
          actions.push({
            value: selectedStatus === 'opt_in' ? 'opt_in_store' : 'store',
            name: 'optin_optout_value',
            license_key: licenseKey,
            type: 'add',
            store_id: storeId
          });
        }
      }

      const defaultBlacklist = getDefaultBlacklistValue(row);
      const selectedBlacklist = blacklistByStoreId[storeId] ?? defaultBlacklist;
      if (selectedBlacklist !== defaultBlacklist) {
        actions.push({
          name: 'blacklisted',
          type: 'create',
          value: selectedBlacklist,
          license_key: licenseKey,
          dealer_id: dealerProfileId,
          store_id: storeId
        });
      }
    });

    return actions;
  }, [
    rows,
    storeStatusById,
    blacklistByStoreId,
    allowPerStoreOptInStatusChange,
    licenseKey,
    dealerProfileId
  ]);

  useEffect(() => {
    onStoreActionsChange?.(storeActions, storeActions.length > 0);
  }, [storeActions, onStoreActionsChange]);

  return (
    <Box className="store-optin-config">
      <Text className="store-optin-config__description">
        Configure blacklist status for each store individually.
      </Text>

      <form onSubmit={onSearchSubmit}>
        <FormControl sx={{ paddingBottom: '10px' }}>
          <Box className="store-optin-config__search">
            <Input
              placeholder="Search stores..."
              value={searchText}
              pr={showClearSearch ? '104px' : '68px'}
              onChange={(event) => setSearchText(event.target.value)}
            />
            {showClearSearch && (
              <Button
                type="button"
                variant="ghost"
                className="store-optin-config__search-clear"
                aria-label="Clear search"
                onClick={onClearSearch}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 13 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M8.3 4.7L4.7 8.3M4.7 4.7L8.3 8.3M12.5 6.5C12.5 9.81371 9.81371 12.5 6.5 12.5C3.18629 12.5 0.5 9.81371 0.5 6.5C0.5 3.18629 3.18629 0.5 6.5 0.5C9.81371 0.5 12.5 3.18629 12.5 6.5Z"
                    stroke="#757575"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Button>
            )}
            <Button
              type="submit"
              variant="ghost"
              className="store-optin-config__search-btn"
              aria-label="Search stores"
            >
              <img src="/images/ui/searchIcon.svg" alt="" />
            </Button>
          </Box>
        </FormControl>
      </form>

      <Box className="store-optin-config__table-wrapper">
        <table className="store-optin-config__table">
          <thead>
            <tr>
              <th>Store Name</th>
              <th>Status</th>
              <th>Blacklist</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={3} className="store-optin-config__loading-cell">
                  <Spinner size="sm" />
                </td>
              </tr>
            )}

            {!isLoading &&
              tableRows.map((row) => {
                const storeId = getStoreId(row);
                const apiStatus = getOptInStatusFromApi(row);
                const currentStatus = allowPerStoreOptInStatusChange
                  ? storeStatusById[storeId] ?? apiStatus
                  : apiStatus;
                const statusMeta =
                  STATUS_OPTIONS.find((option) => option.value === currentStatus) ||
                  STATUS_OPTIONS[1];
                const blacklistValue =
                  blacklistByStoreId[storeId] ?? getDefaultBlacklistValue(row);
                const statusReadOnly = !allowPerStoreOptInStatusChange;

                return (
                  <tr key={storeId}>
                    <td className="store-optin-config__store-name">{getDisplayStoreName(row)}</td>
                    <td>
                      <Box className="store-optin-config__cell-center">
                        {statusReadOnly ? (
                          <Box
                            aria-label={`Transfer status: ${statusMeta.label}`}
                            className="store-optin-config__status-btn store-optin-config__status-btn--readonly"
                            sx={getOptInStatusMenuButtonSx(currentStatus, { isReadOnly: true })}
                          >
                            <Box className="store-optin-config__status-btn-inner">
                              <span className={`store-optin-config__status-dot ${statusMeta.dotClassName}`} />
                              <span className="store-optin-config__status-label">{statusMeta.label}</span>
                              <ChevronDownIcon
                                boxSize={4}
                                className="store-optin-config__status-chevron store-optin-config__status-chevron--readonly"
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Menu placement="bottom-start">
                            <MenuButton
                              className="store-optin-config__status-btn"
                              sx={getOptInStatusMenuButtonSx(currentStatus)}
                            >
                              <Box className="store-optin-config__status-btn-inner">
                                <span className={`store-optin-config__status-dot ${statusMeta.dotClassName}`} />
                                <span className="store-optin-config__status-label">{statusMeta.label}</span>
                                <ChevronDownIcon boxSize={4} className="store-optin-config__status-chevron" />
                              </Box>
                            </MenuButton>
                            <MenuList className="store-optin-config__status-menu">
                              {SELECTABLE_STATUS_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  onClick={() =>
                                    setStoreStatusById((prev) => ({ ...prev, [storeId]: option.value }))
                                  }
                                  className="store-optin-config__status-menu-item"
                                >
                                  <span className={`store-optin-config__status-dot ${option.dotClassName}`} />
                                  <span>{option.label}</span>
                                </MenuItem>
                              ))}
                            </MenuList>
                          </Menu>
                        )}
                      </Box>
                    </td>
                    <td>
                      <Box className="store-optin-config__cell-center">
                        <Switch
                          size="md"
                          sx={BLACKLIST_SWITCH_SX}
                          isChecked={blacklistValue}
                          onChange={(event) =>
                            setBlacklistByStoreId((prev) => ({
                              ...prev,
                              [storeId]: event.target.checked
                            }))
                          }
                        />
                      </Box>
                    </td>
                  </tr>
                );
              })}

            {!isLoading && !dealerId && (
              <tr>
                <td colSpan={3} className="store-optin-config__empty-cell">
                  Dealer is not available to load stores.
                </td>
              </tr>
            )}

            {!isLoading && dealerId && (isApiError || tableRows.length === 0) && (
              <tr>
                <td colSpan={3} className="store-optin-config__empty-cell">
                  No stores found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}
